import { useEventListener, useUnmountEffect } from 'primereact/hooks';
import React, { useContext, useEffect, useRef } from 'react';
import { cn } from '@/helpers/utils/functionality-utils';
import AppFooter from './AppFooter';
import AppSidebar from './AppSidebar';
import AppTopbar from './AppTopbar';
import type { ChildContainerProps, LayoutState, AppTopbarRef } from '@/types';
import { useAppSelector } from '@/store/hooks';
import { LayoutContext } from './context/context';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from 'primereact/button';
import { TbLogout } from 'react-icons/tb';

const Layout = ({ children }: ChildContainerProps) => {
  const { t } = useTranslation('common');
  const { t: translate } = useTranslation('common');
  const nav = useNavigate();
  const { layoutConfig, layoutState, setLayoutState } =
    useContext(LayoutContext);
  // const { setRipple } = useContext(PrimeReactContext);
  const authUserData = useAppSelector((state) => state.user.authUserData);
  const name = authUserData?.data?.name;
  const isCustomer = !!useAppSelector(
    (state) => state?.user?.authUserData.data.customer?.c_id,
  );
  const userFound = !!name;
  const topbarRef = useRef<AppTopbarRef>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] =
    useEventListener({
      type: 'click',
      listener: (event) => {
        const isOutsideClicked = !(
          sidebarRef.current?.isSameNode(event.target as Node) ||
          sidebarRef.current?.contains(event.target as Node) ||
          topbarRef.current?.menubutton?.isSameNode(event.target as Node) ||
          topbarRef.current?.menubutton?.contains(event.target as Node)
        );

        if (isOutsideClicked) {
          hideMenu();
        }
      },
    });

  const pathname = useLocation().pathname;
  const [searchParams] = useSearchParams();
  useEffect(() => {
    hideMenu();
    hideProfileMenu();
  }, [pathname, searchParams]);

  const [
    bindProfileMenuOutsideClickListener,
    unbindProfileMenuOutsideClickListener,
  ] = useEventListener({
    type: 'click',
    listener: (event) => {
      const isOutsideClicked = !(
        topbarRef.current?.topbarmenu?.isSameNode(event.target as Node) ||
        topbarRef.current?.topbarmenu?.contains(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.isSameNode(event.target as Node) ||
        topbarRef.current?.topbarmenubutton?.contains(event.target as Node)
      );

      if (isOutsideClicked) {
        hideProfileMenu();
      }
    },
  });

  const hideMenu = () => {
    setLayoutState((prevLayoutState: LayoutState) => ({
      ...prevLayoutState,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    unbindMenuOutsideClickListener();
    unblockBodyScroll();
  };

  const hideProfileMenu = () => {
    setLayoutState((prevLayoutState: LayoutState) => ({
      ...prevLayoutState,
      profileSidebarVisible: false,
    }));
    unbindProfileMenuOutsideClickListener();
  };

  const blockBodyScroll = (): void => {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  };

  const unblockBodyScroll = (): void => {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi',
        ),
        ' ',
      );
    }
  };

  useEffect(() => {
    if (layoutState?.overlayMenuActive || layoutState?.staticMenuMobileActive) {
      bindMenuOutsideClickListener();
    }
    if (layoutState.staticMenuMobileActive) {
      blockBodyScroll();
    }
  }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive]);

  useEffect(() => {
    if (layoutState.profileSidebarVisible) {
      bindProfileMenuOutsideClickListener();
    }
  }, [layoutState.profileSidebarVisible]);

  useUnmountEffect(() => {
    unbindMenuOutsideClickListener();
    unbindProfileMenuOutsideClickListener();
  });

  const containerClass = cn('layout-wrapper', {
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-static-inactive':
      layoutState.staticMenuDesktopInactive &&
      layoutConfig.menuMode === 'static',
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.staticMenuMobileActive,
    'p-input-filled': layoutConfig.inputStyle === 'filled',
    'p-ripple-disabled': !layoutConfig.ripple,
  });

  if (authUserData.error) {
    return (
      <div className="grid size-full h-screen w-screen place-items-center">
        <div className="flex flex-col items-center gap-3">
          <div className="mt-3 text-3xl">{t('invalidUserMsg1')}</div>
          <div className="size-96">
            <img
              className="size-full object-cover"
              src="/assets/images/customer-dashboard/access-denied.jpg"
              alt="no data"
            />
          </div>
          <div className="mt-3 text-3xl">{t('invalidUserMsg2')}</div>
          <Button
            type="button"
            severity="secondary"
            text
            className="p-link layout-topbar-button"
            onClick={() => {
              nav('/blank', {
                state: {
                  type: 'logout',
                },
              });
            }}
            title={translate('Sign out')}
          >
            <TbLogout className="mr-2 text-3xl sm:mr-0" />
            <span className="ml-2">{translate('Sign out')}</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={containerClass}>
        <AppTopbar ref={topbarRef} />
        {!isCustomer && (
          <div ref={sidebarRef} className="layout-sidebar">
            <AppSidebar />
          </div>
        )}
        <div className={cn('layout-main-container', isCustomer && '!ml-0')}>
          <div className="layout-main flex *:w-full *:grow">
            {userFound && children}
          </div>
          <AppFooter />
        </div>
        <div className="layout-mask"></div>
      </div>
    </React.Fragment>
  );
};

export default Layout;
